import React from "react";
import {
  Iconography,
  IconographySizes,
  Loader,
  LoaderSizes,
  // Pagination,
} from "@hid-galaxy-ui/galaxy-react";
import { getProjectsListSagaAction } from "../../../../redux/saga/projects/saga-actions";
import { useAppDispatch, useAppSelector } from "../../../../redux";
import moment from "moment";
import { useNavigate } from "react-router";
import { Project, Technology } from "../../../../redux/slices/projects/Types";
import { ProjectForDisplay, TechnologyWithStatus } from "./types";
import QuickNavigation from "../../../../common/components/QuickNavigation";
import ProjectsPageBanner from "./components/ProjectsPageBanner";
import { IntegrationStatusLabel } from "./project-details/integration-steps/shared/StatusTag";
import CircleMinus from "../../../../common/assets/icons/CircleMinus";
import Timer from "../../../../common/assets/icons/Timer";
import { getTechnologyBadgeVariant } from "../../../../utils/technologyBadge";
import { useTimelineStepPermissions } from "../../../../hooks/useTimelineStepPermissions";
import "./Projects.scss";

const enrichTechnology = (tech: Technology): TechnologyWithStatus => {
  const earliestIntegration =
    tech.integrations.length > 0
      ? tech.integrations.reduce((earliest, current) => {
          const earliestDate = moment(earliest.created_at);
          const currentDate = moment(current.created_at);
          return currentDate.isBefore(earliestDate) ? current : earliest;
        })
      : null;

  const latestIntegration =
    tech.integrations.length > 0
      ? tech.integrations.reduce((latest, current) => {
          const latestDate = moment(latest.updated_at);
          const currentDate = moment(current.updated_at);
          return currentDate.isAfter(latestDate) ? current : latest;
        })
      : null;

  const status = "In Progress";

  return {
    ...tech,
    status,
    start_date: earliestIntegration?.created_at || null,
    completion_date: null,
    updated_at: latestIntegration?.updated_at || null,
  };
};

const mapProjectsForDisplay = (projects: Project[]): ProjectForDisplay[] => {
  return projects.map((project) => ({
    project_name: project.project_name,
    end_user_use_cases: project.technologies[0]?.end_user_use_cases || "",
    technology: project.technologies.map(enrichTechnology),
  }));
};

const formatLastUpdated = (projects: ProjectForDisplay[]): string => {
  const dates = projects
    .flatMap((project) => project.technology.map((tech) => tech.updated_at))
    .filter((date): date is string => Boolean(date));

  const latest = dates.sort().at(-1);
  const format = "MMM DD YYYY - hh:mm A";

  return latest ? moment(latest).format(format) : moment().format(format);
};

const Projects = () => {
  // const [currentPageNumber, setCurrentPageNumber] = React.useState<number>(1);
  const { projectsList, status } = useAppSelector((state) => state.projects);
  const { partnerDetails } = useAppSelector((state) => state.partner_details);
  const dispatch = useAppDispatch();

  // const handlePageChange = (clickedPageNumber: number | null) => {
  //   setCurrentPageNumber(clickedPageNumber);
  // };

  // const pageSize = 5;
  const projectsForDisplay = React.useMemo(
    () => mapProjectsForDisplay(projectsList),
    [projectsList]
  );
  // const totalRecords = projectsForDisplay.length;
  const lastUpdated = React.useMemo(
    () => formatLastUpdated(projectsForDisplay),
    [projectsForDisplay]
  );

  // const paginatedData = projectsForDisplay.slice(
  //   (currentPageNumber - 1) * pageSize,
  //   currentPageNumber * pageSize
  // );

  React.useEffect(() => {
    if (partnerDetails) {
      dispatch(getProjectsListSagaAction(partnerDetails.ID));
    }
  }, [partnerDetails, dispatch]);

  return (
    <div className="projects-list-page">
      <div className="projects-container">
        <span className="projects-breadcrumb">Projects</span>
        <ProjectsPageBanner lastUpdated={lastUpdated} />

        <div className="projects-panel">
          {status.projectsListLoading ? (
            <div className="projects-panel__loading">
              <Loader variant={LoaderSizes.large} />
            </div>
          ) : projectsForDisplay.length > 0 ? (
            <>
              <div className="projects-grid">
                {projectsForDisplay.map((project) => (
                  <ProjectCard
                    project={project}
                    key={project.project_name}
                    partnerID={partnerDetails?.ID}
                  />
                ))}
              </div>
              {/* <Pagination
                currentPageNumber={currentPageNumber}
                totalRecords={totalRecords}
                pageSize={pageSize}
                border={true}
                onPageNumberClick={handlePageChange}
              /> */}
            </>
          ) : (
            <div className="projects-panel__empty">
              <p>You have no current projects</p>
            </div>
          )}
        </div>
      </div>
      <QuickNavigation variant="flyout" />
    </div>
  );
};

const ProjectCard: React.FC<{
  project: ProjectForDisplay;
  partnerID?: number;
}> = ({ project, partnerID }) => {
  const navigate = useNavigate();
  const { canAccessTimeline } = useTimelineStepPermissions();

  const handleNavigate = (selectedTechnology?: string) => {
    if (!partnerID || !canAccessTimeline) return;

    if (selectedTechnology) {
      try {
        const decodedName = project.project_name;
        sessionStorage.setItem(
          `project-active-technology:${partnerID}:${decodedName}`,
          selectedTechnology
        );
        sessionStorage.setItem(
          `project-active-technology:${decodedName}`,
          selectedTechnology
        );
      } catch {
        // Ignore storage failures
      }
    }

    navigate(
      `/partner/${partnerID}/dashboard/projects/${encodeURIComponent(
        project.project_name
      )}`,
      selectedTechnology ? { state: { selectedTechnology } } : undefined
    );
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!canAccessTimeline) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleNavigate();
    }
  };

  const handleTechnologyClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    technologyName: string
  ) => {
    event.stopPropagation();
    if (!canAccessTimeline) return;
    handleNavigate(technologyName);
  };

  return (
    <div
      className={`project-list-card${
        !canAccessTimeline ? " project-list-card--disabled" : ""
      }`}
      role={canAccessTimeline ? "button" : undefined}
      tabIndex={canAccessTimeline ? 0 : undefined}
      aria-label={
        canAccessTimeline
          ? `Open ${project.project_name} project details`
          : `${project.project_name} project details unavailable`
      }
      aria-disabled={!canAccessTimeline}
      onClick={canAccessTimeline ? () => handleNavigate() : undefined}
      onKeyDown={canAccessTimeline ? handleKeyDown : undefined}
    >
      <div className="project-list-card__header">
        <h2 className="project-list-card__title">{project.project_name}</h2>
      </div>

      {project.technology.length > 0 ? (
        <div className="project-list-card__badges">
          {project.technology.map((technology) => {
            const variant = getTechnologyBadgeVariant(technology.technology);

            return (
              <button
                key={`${project.project_name}-${technology.technology_id}`}
                type="button"
                className={`project-list-card__badge project-list-card__badge--${variant}`}
                disabled={!canAccessTimeline}
                onClick={(event) =>
                  handleTechnologyClick(event, technology.technology)
                }
              >
                {technology.technology}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

const getPhaseStageLabel = (phaseStage: string | null | undefined): string => {
  if (!phaseStage || phaseStage === "NA") {
    return "Not Started";
  }

  return phaseStage;
};

const getPhaseStatusIcon = (
  phaseStatus: string | null | undefined
): IntegrationStatusLabel => {
  if (!phaseStatus) {
    return "Not applicable";
  }

  const normalized = phaseStatus.toLowerCase().trim();

  if (normalized === "completed") {
    return "Completed";
  }

  if (normalized === "in progress") {
    return "In progress";
  }

  if (normalized === "on hold") {
    return "On hold";
  }

  if (normalized === "not applicable") {
    return "Not applicable";
  }

  if (normalized === "not started") {
    return "Not started";
  }

  return "Not applicable";
};

export const StatusIcon = ({ status }: { status: IntegrationStatusLabel }) => {
  let className = "not-applicable";

  if (
    status === "Completed" ||
    status === "Validated - Full approval" ||
    status === "Validated - Conditional approval"
  ) {
    className = "completed";
  } else if (status === "In progress") {
    className = "in-progress";
  } else if (status === "On hold") {
    className = "on-hold";
  } else if (status === "Not applicable") {
    className = "not-applicable";
  } else if (status === "Validation - Failed") {
    className = "failed";
  }

  const renderIcon = () => {
    switch (status) {
      case "Completed":
      case "Validated - Full approval":
      case "Validated - Conditional approval":
        return <Iconography icon="circleCheck" size={IconographySizes.Medium} />;
      case "In progress":
        return <Timer />;
      case "On hold":
        return (
          <Iconography icon="circleExclamation" size={IconographySizes.Medium} />
        );
      case "Validation - Failed":
        return <Iconography icon="circleX" size={IconographySizes.Medium} />;
      case "Not applicable":
      default:
        return <CircleMinus />;
    }
  };

  return (
    <span className={`step-status-icon ${className}`} aria-hidden="true">
      {renderIcon()}
    </span>
  );
};

export { getPhaseStageLabel, getPhaseStatusIcon };

export default Projects;
